import { AggregationDefinition, DataSet, DataSource, MonitoringSchedule, Rule, RuleRun } from './types';

const DEFAULT_DS_FIELDS = {
  standard_schema: [] as DataSource['standard_schema'],
  allowed_source_types: ['csv', 'xlsx', 'xls', 'txt'] as string[],
  icon_key: 'custom',
  color_key: 'indigo',
};

export const mockDataSources: DataSource[] = [
  {
    id: 'ds-001',
    name: 'Sales Transactions',
    description: 'Daily sales feed from POS systems across all stores',
    entity_id: 'entity-001',
    created_by: 'grcadmin',
    created_at: '2026-04-10T10:00:00Z',
    dataset_count: 4,
    total_rows: 2_840_000,
    ...DEFAULT_DS_FIELDS,
  },
  {
    id: 'ds-002',
    name: 'Vendor Invoices',
    description: 'AP vendor invoices for compliance & duplicate-payment checks',
    entity_id: 'entity-001',
    created_by: 'grcadmin',
    created_at: '2026-04-12T08:30:00Z',
    dataset_count: 2,
    total_rows: 145_320,
    ...DEFAULT_DS_FIELDS,
  },
  {
    id: 'ds-003',
    name: 'Employee Expense Claims',
    description: 'T&E expense claims uploaded by finance',
    entity_id: 'entity-001',
    created_by: 'finance.lead',
    created_at: '2026-05-02T11:15:00Z',
    dataset_count: 1,
    total_rows: 38_200,
    ...DEFAULT_DS_FIELDS,
  },
];

export const mockDataSets: DataSet[] = [
  {
    id: 'dset-001',
    data_source_id: 'ds-001',
    file_name: 'sales_2026_q1.csv',
    format: 'csv',
    s3_key: 's3://anygrc-data/entity-001/ds-001/sales_2026_q1.csv',
    row_count: 980_412,
    size_bytes: 184_320_000,
    uploaded_by: 'grcadmin',
    uploaded_at: '2026-04-10T10:05:00Z',
    version: 1,
    status: 'Ready',
    kind: 'raw',
    upload_mode: 'new',
    columns: [
      { name: 'txn_id', data_type: 'text', null_count: 0, distinct_count: 980412 },
      { name: 'txn_date', data_type: 'date', null_count: 0 },
      { name: 'store_id', data_type: 'categorical', distinct_count: 142 },
      { name: 'product_sku', data_type: 'text', distinct_count: 8420 },
      { name: 'quantity', data_type: 'numeric', null_count: 12 },
      { name: 'unit_price', data_type: 'continuous', null_count: 0 },
      { name: 'discount_pct', data_type: 'continuous', null_count: 84_211 },
      { name: 'total_amount', data_type: 'continuous', null_count: 0 },
      { name: 'channel', data_type: 'categorical', distinct_count: 4, sample_values: ['Online', 'In-Store', 'Mobile', 'Partner'] },
    ],
  },
  {
    id: 'dset-002',
    data_source_id: 'ds-001',
    file_name: 'sales_2026_q2.csv',
    format: 'csv',
    s3_key: 's3://anygrc-data/entity-001/ds-001/sales_2026_q2.csv',
    row_count: 1_212_000,
    size_bytes: 226_410_000,
    uploaded_by: 'grcadmin',
    uploaded_at: '2026-05-15T09:00:00Z',
    version: 1,
    status: 'Ready',
    kind: 'raw',
    upload_mode: 'new',
    columns: [
      { name: 'txn_id', data_type: 'text' },
      { name: 'txn_date', data_type: 'date' },
      { name: 'store_id', data_type: 'categorical' },
      { name: 'product_sku', data_type: 'text' },
      { name: 'quantity', data_type: 'numeric' },
      { name: 'unit_price', data_type: 'continuous' },
      { name: 'discount_pct', data_type: 'continuous' },
      { name: 'total_amount', data_type: 'continuous' },
      { name: 'channel', data_type: 'categorical' },
    ],
  },
  {
    id: 'dset-003',
    data_source_id: 'ds-001',
    file_name: 'agg_sales_by_store_month.parquet',
    format: 'parquet',
    s3_key: 's3://anygrc-data/entity-001/ds-001/aggregations/agg_sales_by_store_month.parquet',
    row_count: 4_260,
    size_bytes: 320_000,
    uploaded_by: 'system',
    uploaded_at: '2026-05-16T02:10:00Z',
    version: 1,
    status: 'Ready',
    kind: 'aggregation',
    upload_mode: 'new',
    parent_dataset_id: 'dset-002',
    columns: [
      { name: 'store_id', data_type: 'categorical' },
      { name: 'month', data_type: 'date' },
      { name: 'total_sales', data_type: 'continuous' },
      { name: 'txn_count', data_type: 'numeric' },
      { name: 'avg_ticket', data_type: 'continuous' },
    ],
  },
  {
    id: 'dset-004',
    data_source_id: 'ds-002',
    file_name: 'vendor_invoices_apr_may.xlsx',
    format: 'xlsx',
    s3_key: 's3://anygrc-data/entity-001/ds-002/vendor_invoices_apr_may.xlsx',
    row_count: 145_320,
    size_bytes: 42_100_000,
    uploaded_by: 'grcadmin',
    uploaded_at: '2026-05-18T14:22:00Z',
    version: 2,
    status: 'Ready',
    kind: 'raw',
    upload_mode: 'new',
    columns: [
      { name: 'invoice_id', data_type: 'text' },
      { name: 'vendor_id', data_type: 'categorical' },
      { name: 'invoice_date', data_type: 'date' },
      { name: 'amount', data_type: 'continuous' },
      { name: 'currency', data_type: 'categorical' },
      { name: 'po_number', data_type: 'text' },
      { name: 'status', data_type: 'categorical', sample_values: ['Paid', 'Pending', 'Disputed'] },
    ],
  },
  {
    id: 'dset-005',
    data_source_id: 'ds-003',
    file_name: 'expenses_may_2026.csv',
    format: 'csv',
    s3_key: 's3://anygrc-data/entity-001/ds-003/expenses_may_2026.csv',
    row_count: 38_200,
    size_bytes: 6_840_000,
    uploaded_by: 'finance.lead',
    uploaded_at: '2026-05-20T09:48:00Z',
    version: 1,
    status: 'Profiling',
    kind: 'raw',
    upload_mode: 'new',
    columns: [
      { name: 'claim_id', data_type: 'text' },
      { name: 'employee_id', data_type: 'categorical' },
      { name: 'expense_date', data_type: 'date' },
      { name: 'category', data_type: 'categorical', sample_values: ['Travel', 'Meals', 'Lodging', 'Misc'] },
      { name: 'amount', data_type: 'continuous' },
      { name: 'receipt_attached', data_type: 'categorical' },
    ],
  },
];

export const mockAggregations: AggregationDefinition[] = [
  {
    id: 'agg-001',
    name: 'Sales by Store × Month',
    source_dataset_id: 'dset-002',
    source_dataset_name: 'sales_2026_q2.csv',
    type: 'multi_group_by',
    group_by_columns: ['store_id', 'txn_date'],
    measure_column: 'total_amount',
    measure_aggregation: 'sum',
    output_dataset_id: 'dset-003',
    created_by: 'grcadmin',
    created_at: '2026-05-16T01:50:00Z',
    last_run_at: '2026-05-16T02:10:00Z',
    status: 'Ready',
  },
  {
    id: 'agg-002',
    name: 'Vendor spend Top-50 (Pivot by month)',
    source_dataset_id: 'dset-004',
    source_dataset_name: 'vendor_invoices_apr_may.xlsx',
    type: 'pivot',
    group_by_columns: ['vendor_id'],
    pivot_column: 'invoice_date',
    measure_column: 'amount',
    measure_aggregation: 'sum',
    created_by: 'grcadmin',
    created_at: '2026-05-19T10:30:00Z',
    status: 'Draft',
  },
  {
    id: 'agg-003',
    name: '30-day Moving Average — Daily Sales',
    source_dataset_id: 'dset-002',
    source_dataset_name: 'sales_2026_q2.csv',
    type: 'moving_average',
    group_by_columns: ['txn_date'],
    measure_column: 'total_amount',
    measure_aggregation: 'sum',
    window_size: 30,
    created_by: 'analyst.k',
    created_at: '2026-05-20T08:15:00Z',
    status: 'Ready',
    last_run_at: '2026-05-20T08:22:00Z',
  },
  {
    id: 'agg-004',
    name: 'Channel Part-to-Whole — Q2',
    source_dataset_id: 'dset-002',
    source_dataset_name: 'sales_2026_q2.csv',
    type: 'part_to_whole',
    group_by_columns: ['channel'],
    measure_column: 'total_amount',
    measure_aggregation: 'sum',
    created_by: 'analyst.k',
    created_at: '2026-05-20T08:30:00Z',
    status: 'Ready',
  },
];

export const mockRules: Rule[] = [
  {
    id: 'rule-001',
    name: 'High-value transactions (> ₹5L)',
    description: 'Flag any single sales transaction over ₹5,00,000 for manual review',
    entity_id: 'entity-001',
    severity: 'High',
    tags: ['fraud', 'monitoring'],
    created_by: 'grcadmin',
    created_at: '2026-05-10T09:00:00Z',
    last_run_at: '2026-05-20T06:00:00Z',
    status: 'Active',
    sources: [],
    steps: [
      {
        id: 'step-001-1',
        step_number: 1,
        target_dataset_id: 'dset-002',
        target_dataset_name: 'sales_2026_q2.csv',
        column: 'total_amount',
        column_type: 'continuous',
        operator: 'gt',
        params: ['500000'],
      },
    ],
  },
  {
    id: 'rule-002',
    name: 'Outlier store revenue (>2σ above mean)',
    description: 'Stores with monthly revenue more than 2 standard deviations above the mean',
    entity_id: 'entity-001',
    severity: 'Medium',
    tags: ['anomaly'],
    created_by: 'analyst.k',
    created_at: '2026-05-17T14:00:00Z',
    last_run_at: '2026-05-20T06:00:00Z',
    status: 'Active',
    sources: [],
    steps: [
      {
        id: 'step-002-1',
        step_number: 1,
        target_dataset_id: 'dset-003',
        target_dataset_name: 'agg_sales_by_store_month.parquet',
        column: 'total_sales',
        column_type: 'continuous',
        operator: 'std_dev_above',
        params: ['2'],
      },
    ],
  },
  {
    id: 'rule-003',
    name: 'Duplicate vendor invoices',
    description: '3-step chain: filter paid invoices → group by (vendor, amount, date) → flag groups with > 1 invoice',
    entity_id: 'entity-001',
    severity: 'Critical',
    tags: ['fraud', 'duplicate-payment'],
    created_by: 'grcadmin',
    created_at: '2026-05-18T11:30:00Z',
    last_run_at: '2026-05-20T06:05:00Z',
    status: 'Active',
    sources: [],
    steps: [
      {
        id: 'step-003-1',
        step_number: 1,
        target_dataset_id: 'dset-004',
        target_dataset_name: 'vendor_invoices_apr_may.xlsx',
        column: 'status',
        column_type: 'categorical',
        operator: 'in_list',
        params: ['Paid'],
        description: 'Keep only paid invoices',
      },
      {
        id: 'step-003-2',
        step_number: 2,
        target_dataset_id: 'dset-004',
        target_dataset_name: 'vendor_invoices_apr_may.xlsx',
        column: 'amount',
        column_type: 'continuous',
        operator: 'gt',
        params: ['1000'],
        description: 'Ignore tiny invoices (< 1000)',
      },
      {
        id: 'step-003-3',
        step_number: 3,
        target_dataset_id: 'dset-004',
        target_dataset_name: 'vendor_invoices_apr_may.xlsx',
        column: 'invoice_date',
        column_type: 'date',
        operator: 'within_last_days',
        params: ['90'],
        description: 'Only recent 90 days',
      },
    ],
  },
  {
    id: 'rule-004',
    name: 'Suspicious expense categories',
    description: 'Expense claims in Misc category over ₹50,000',
    entity_id: 'entity-001',
    severity: 'Low',
    tags: ['t&e', 'audit'],
    created_by: 'finance.lead',
    created_at: '2026-05-20T10:00:00Z',
    status: 'Draft',
    sources: [],
    steps: [
      {
        id: 'step-004-1',
        step_number: 1,
        target_dataset_id: 'dset-005',
        target_dataset_name: 'expenses_may_2026.csv',
        column: 'category',
        column_type: 'categorical',
        operator: 'in_list',
        params: ['Misc'],
      },
      {
        id: 'step-004-2',
        step_number: 2,
        target_dataset_id: 'dset-005',
        target_dataset_name: 'expenses_may_2026.csv',
        column: 'amount',
        column_type: 'continuous',
        operator: 'gt',
        params: ['50000'],
      },
    ],
  },
];

export const mockSchedules: MonitoringSchedule[] = [
  {
    id: 'sch-001',
    name: 'Nightly — All Active Rules',
    rule_ids: ['rule-001', 'rule-002', 'rule-003'],
    mode: 'cron',
    cron_expression: '0 6 * * *',
    enabled: true,
    last_run_at: '2026-05-20T06:00:00Z',
    next_run_at: '2026-05-21T06:00:00Z',
  },
  {
    id: 'sch-002',
    name: 'On every Vendor Invoice upload',
    rule_ids: ['rule-003'],
    mode: 'on_upload',
    enabled: true,
    last_run_at: '2026-05-18T14:25:00Z',
  },
  {
    id: 'sch-003',
    name: 'Hourly — High-value txn scan',
    rule_ids: ['rule-001'],
    mode: 'interval',
    interval_minutes: 60,
    enabled: false,
  },
];

export const mockRuns: RuleRun[] = [
  {
    id: 'run-001',
    rule_id: 'rule-003',
    rule_name: 'Duplicate vendor invoices',
    triggered_by: 'Schedule',
    started_at: '2026-05-20T06:05:00Z',
    finished_at: '2026-05-20T06:06:42Z',
    status: 'Completed',
    rows_in: 145_320,
    rows_matched: 84,
    output_file: 'rule-003_run-001.parquet',
    output_size_bytes: 41_200,
  },
  {
    id: 'run-002',
    rule_id: 'rule-001',
    rule_name: 'High-value transactions (> ₹5L)',
    triggered_by: 'Schedule',
    started_at: '2026-05-20T06:00:00Z',
    finished_at: '2026-05-20T06:01:18Z',
    status: 'Completed',
    rows_in: 1_212_000,
    rows_matched: 1_842,
    output_file: 'rule-001_run-002.parquet',
    output_size_bytes: 184_000,
  },
  {
    id: 'run-003',
    rule_id: 'rule-002',
    rule_name: 'Outlier store revenue (>2σ above mean)',
    triggered_by: 'Schedule',
    started_at: '2026-05-20T06:00:10Z',
    finished_at: '2026-05-20T06:00:32Z',
    status: 'Completed',
    rows_in: 4_260,
    rows_matched: 11,
    output_file: 'rule-002_run-003.parquet',
    output_size_bytes: 8_400,
  },
  {
    id: 'run-004',
    rule_id: 'rule-001',
    rule_name: 'High-value transactions (> ₹5L)',
    triggered_by: 'Manual',
    started_at: '2026-05-19T15:42:00Z',
    finished_at: '2026-05-19T15:43:11Z',
    status: 'Completed',
    rows_in: 980_412,
    rows_matched: 1_402,
    output_file: 'rule-001_run-004.parquet',
    output_size_bytes: 142_000,
  },
  {
    id: 'run-005',
    rule_id: 'rule-004',
    rule_name: 'Suspicious expense categories',
    triggered_by: 'Manual',
    started_at: '2026-05-20T11:00:00Z',
    finished_at: '2026-05-20T11:00:04Z',
    status: 'Failed',
    rows_in: 0,
    rows_matched: 0,
    error_message: 'Source dataset still profiling — retry once status is Ready',
  },
];

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const formatNumber = (n: number): string => n.toLocaleString('en-IN');

export const formatDate = (iso?: string): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-GB');
};
